import React, { useContext } from 'react';
import {
  Box,
  VStack,
  Icon,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  Flex,
  Text,
  Tooltip,
  Divider,
  useColorModeValue,
  IconButton,
  Badge,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  HStack,
  Button
} from '@chakra-ui/react';
import {
  MdDashboard,
  MdPeople,
  MdLibraryBooks,
  MdEvent,
  MdSettings,
  MdLogout,
  MdInsertChart,
  MdHome,
  MdFeedback,
  MdNotifications,
  MdMenu,
  MdClose,
  MdAssignment
} from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiClock } from 'react-icons/fi';
import AuthContext from '../services/AuthContext';

const SidebarInstructor = () => {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile drawer state
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  // Responsive breakpoints
  const isMobile = useBreakpointValue({ base: true, md: false });
  const sidebarWidth = useBreakpointValue({ base: '100%', md: '70px' });
  const showMobileMenu = useBreakpointValue({ base: true, md: false });

  // Platform colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue(secondaryColor, "gray.900");
  const hoverBg = useColorModeValue(`${primaryColor}20`, "yellow.700");
  const activeBg = useColorModeValue(primaryColor, "yellow.600");
  const textColor = useColorModeValue("white", "gray.100");

  const adminMenuItems = [
    { icon: MdLibraryBooks, path: '/instructor/courses', label: "المواد" },
    { icon: MdEvent, path: '/instructor/events', label: 'الأحداث' },
    { icon: MdEvent, path: '/instructor/abmanagement', label: 'الأحداث' },
    // { icon: MdFeedback, path: '/instructor/qcm', label: 'الإختبارات' },
    { icon: MdSettings, path: '/instructor/upload', label: 'ملفات التلاميذ' },
    { icon: FiClock, path: '/attendance/control', label: 'رصد الغياب' },
    ...(user?.role === 'admin' ? [{ icon: MdAssignment, path: '/admin/recrutement', label: 'طلبات الانتدابات' }] : []),
  ];

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      onDrawerClose();
    }
  };

  // Mobile Hamburger Menu Button
  const MobileMenuButton = () => (
    <Box
      position="fixed"
      top="4"
      right="4"
      zIndex="overlay"
      display={{ base: 'block', md: 'none' }}
    >
      <IconButton
        aria-label="فتح القائمة"
        icon={<MdMenu />}
        onClick={onDrawerOpen}
        size="lg"
        bg={bgColor}
        color={textColor}
        _hover={{ bg: hoverBg }}
        borderRadius="xl"
        boxShadow="lg"
        border="2px solid"
        borderColor={primaryColor}
      />
    </Box>
  );

  // Sidebar Content Component (reusable for both desktop and mobile)
  const SidebarContent = ({ onItemClick = () => { } }) => (
    <Flex direction="column" height="100%" justifyContent="space-between" py={4}>
      {/* Top Section */}
      <VStack spacing={6}>
        {/* Logo */}
        <Box p={2}>
          <Image
            src="/assets/logo.png"
            alt="شعار"
            boxSize="40px"
            objectFit="contain"
            fallbackSrc="https://ui-avatars.com/api/?name=C&background=F2C919&color=2C3E50"
            mx="auto"
          />
        </Box>

        {/* Navigation Icons */}
        <VStack
          spacing={4}
          width="100%"
          overflowY="auto"
          maxH="calc(100vh - 250px)"
          css={{
            '&::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
          }}
        >
          {adminMenuItems.map((item) => (
            <Box key={item.path} width="100%">
              {isMobile ? (
                // Mobile: Show text with icons
                <Button
                  leftIcon={<item.icon />}
                  variant="ghost"
                  size="lg"
                  width="100%"
                  justifyContent="flex-start"
                  color={isActive(item.path) ? secondaryColor : textColor}
                  bg={isActive(item.path) ? activeBg : "transparent"}
                  _hover={{
                    bg: isActive(item.path) ? activeBg : hoverBg,
                    color: isActive(item.path) ? secondaryColor : textColor
                  }}
                  onClick={() => {
                    handleMenuItemClick(item.path);
                    onItemClick();
                  }}
                  borderRadius="xl"
                  fontSize="md"
                  fontWeight="600"
                  py={6}
                  px={4}
                  dir="rtl"
                  minH="48px" // Touch-friendly minimum height
                >
                  {item.label}
                </Button>
              ) : (
                // Desktop: Icon only with tooltip
                <Tooltip
                  label={item.label}
                  placement="left"
                  bg={secondaryColor}
                  color="white"
                  hasArrow
                >
                  <Box
                    position="relative"
                    width="100%"
                    display="flex"
                    justifyContent="center"
                  >
                    <IconButton
                      aria-label={item.label}
                      icon={<item.icon />}
                      variant="ghost"
                      size="lg"
                      color={isActive(item.path) ? secondaryColor : textColor}
                      bg={isActive(item.path) ? activeBg : "transparent"}
                      _hover={{
                        bg: isActive(item.path) ? activeBg : hoverBg,
                        color: isActive(item.path) ? secondaryColor : textColor
                      }}
                      onClick={() => handleMenuItemClick(item.path)}
                      borderRadius="xl"
                      minH="48px"
                      minW="48px"
                    />
                    {isActive(item.path) && (
                      <Box
                        position="absolute"
                        right="0"
                        top="0"
                        bottom="0"
                        width="3px"
                        bg={secondaryColor}
                        borderRadius="full"
                      />
                    )}
                  </Box>
                </Tooltip>
              )}
            </Box>
          ))}
        </VStack>
      </VStack>

      {/* Bottom Section */}
      <VStack spacing={4}>
        {/* Home Button */}
        {isMobile ? (
          <Button
            leftIcon={<MdHome />}
            variant="ghost"
            size="lg"
            width="100%"
            justifyContent="flex-start"
            color={textColor}
            _hover={{ bg: hoverBg }}
            onClick={() => {
              handleMenuItemClick('/');
              onItemClick();
            }}
            borderRadius="xl"
            fontSize="md"
            fontWeight="600"
            py={6}
            px={4}
            dir="rtl"
            minH="48px"
          >
            الصفحة الرئيسية
          </Button>
        ) : (
          <Tooltip label="الصفحة الرئيسية" placement="left" bg={secondaryColor} color="white" hasArrow>
            <IconButton
              aria-label="الصفحة الرئيسية"
              icon={<MdHome />}
              variant="ghost"
              size="lg"
              color={textColor}
              _hover={{ bg: hoverBg }}
              onClick={() => handleMenuItemClick('/')}
              minH="48px"
              minW="48px"
            />
          </Tooltip>
        )}

        {/* User Avatar */}
        <Menu>
          <MenuButton>
            {isMobile ? (
              <HStack
                spacing={3}
                p={3}
                borderRadius="xl"
                _hover={{ bg: hoverBg }}
                cursor="pointer"
                width="100%"
                minH="48px"
              >
                <Avatar
                  size="sm"
                  name={user?.username}
                  bg={primaryColor}
                  color={secondaryColor}
                  fontWeight="bold"
                />
                <Text color={textColor} fontSize="md" fontWeight="600">
                  {user?.username}
                </Text>
              </HStack>
            ) : (
              <Avatar
                size="sm"
                name={user?.username}
                bg={primaryColor}
                color={secondaryColor}
                fontWeight="bold"
                cursor="pointer"
              />
            )}
          </MenuButton>
          <MenuList
            dir="rtl"
            bg="white"
            color="gray.800"
            borderColor="gray.200"
            minWidth="150px"
            borderRadius="xl"
            boxShadow="xl"
          >
            <MenuItem
              icon={<MdLogout />}
              onClick={logout}
              _hover={{ bg: "red.50", color: "red.600" }}
              borderRadius="lg"
              mx={2}
              my={1}
              minH="44px"
              fontSize="md"
            >
              تسجيل الخروج
            </MenuItem>
          </MenuList>
        </Menu>
      </VStack>
    </Flex>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <MobileMenuButton />

      {/* Desktop Sidebar */}
      <Box
        position="fixed"
        top="0"
        right="0"
        width={sidebarWidth}
        height="100vh"
        bg={bgColor}
        color={textColor}
        zIndex="sticky"
        borderLeft="3px solid"
        borderLeftColor={primaryColor}
        dir="rtl"
        display={{ base: 'none', md: 'block' }}
      >
        <SidebarContent />
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        placement="right"
        onClose={onDrawerClose}
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent
          bg={bgColor}
          color={textColor}
          dir="rtl"
        >
          <DrawerCloseButton
            size="lg"
            color={textColor}
            _hover={{ bg: hoverBg }}
            borderRadius="xl"
            top="4"
            right="4"
          />
          <DrawerHeader borderBottomWidth="1px" borderBottomColor={primaryColor}>
            <HStack spacing={3}>
              <Image
                src="/assets/logo.png"
                alt="شعار"
                boxSize="32px"
                objectFit="contain"
                fallbackSrc="https://ui-avatars.com/api/?name=C&background=F2C919&color=2C3E50"
              />
              <Text fontSize="lg" fontWeight="bold">
                لوحة المدرس
              </Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody p={4}>
            <SidebarContent onItemClick={onDrawerClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default SidebarInstructor;

